import { useEffect, useState } from 'react'
import {
  api,
  type FeedFile,
  type JobHistory as History,
  type JobProduct,
  type JobRun,
  type JobRunDetail,
} from '../api'
import { JOB_STATUS, JobLine } from '../components/JobLine'
import { type MenuItem, RowMenu } from '../components/RowMenu'
import { OUTCOME } from './HoldingsFeed'

/**
 * Scheduled Jobs — IMPLEMENTATION.md decision 44.
 *
 * Every run the queue keeps, a month of them, for everyone signed in: what the
 * appliance did on its own is not a privileged fact. Narrowed by job, state and the
 * days a run was queued, in the query, so a filter reaches every run kept.
 *
 * **A run's menu offers Details alone.** Details lists what the run produced, fund by
 * fund, and each row's menu opens it: the snapshot a pass of automatic loading loaded
 * and its verdicts, the verdicts monitoring made, or the document a client sent. A
 * handful of shortcuts on the list picked out some funds before anybody had seen them
 * all. A pass of automatic loading lists every file it handled, and why a file did
 * not load.
 */

const PAGE = 50
// The most the server lists at once; past it, the filters reach the rest.
const MOST = 200
/** What opens one thing a run produced, from its row's menu in Details. */
const PRODUCT_ACTION: Record<JobProduct['type'], string> = {
  snapshot: 'Open snapshot',
  run: 'Open verdicts',
  document: 'Download the document',
  proposals: 'Go to the proposals',
  obligation: 'Open cure obligation',
}

interface Filters {
  kind: string
  state: string
  from: string
  to: string
}
const NONE: Filters = { kind: '', state: '', from: '', to: '' }

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** The instant a day starts, in the reader's own time zone. */
function dayStarts(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString()
}

/** The instant the day after it starts. */
function dayEnds(day: string): string {
  const next = new Date(`${day}T00:00:00`)
  next.setDate(next.getDate() + 1)
  return next.toISOString()
}

/** A reason as the server words it, as a sentence. */
function sentence(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function snapshotLabel(s: { fund: string; as_of: string }): string {
  return `${s.fund} holdings as at ${s.as_of.slice(0, 10)}`
}

type CollectedFile = NonNullable<JobRunDetail['files']>[number]

function when(at: string | null): string {
  return at ? new Date(at).toLocaleString() : '—'
}

function took(run: JobRun): string | null {
  if (!run.started_at || !run.finished_at) return null
  const seconds = Math.max(0, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000))
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

export function JobHistory({
  onOpen,
  canOpen = () => true,
  canRetryFiles = false,
  onMapLayout,
  onOpenVerdicts,
}: {
  /** Go to what a run produced. */
  onOpen: (product: JobProduct) => void
  /** Whether this reader may open what a kind of product leads to. */
  canOpen?: (type: JobProduct['type']) => boolean
  /** An administrator may have a refused or waiting file read again. */
  canRetryFiles?: boolean
  /** Go to Load a snapshot, where a file's layout is mapped, for somebody who may load one. */
  onMapLayout?: () => void
  /** Open the verdicts that judged a snapshot a run loaded. */
  onOpenVerdicts?: (snapshotId: string) => void
}) {
  const [filters, setFilters] = useState<Filters>(NONE)
  const [shown, setShown] = useState(PAGE)
  const [history, setHistory] = useState<History | null>(null)
  const [reads, setReads] = useState(0)
  const [open, setOpen] = useState<JobRunDetail | null>(null)
  const [opening, setOpening] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const filtered = Boolean(filters.kind || filters.state || filters.from || filters.to)

  // The runs already listed stay while the next set is fetched, so a filter being
  // set does not empty the page under it.
  useEffect(() => {
    let live = true
    setError(null)
    api
      .jobHistory({
        kind: filters.kind || undefined,
        state: filters.state || undefined,
        queuedFrom: filters.from ? dayStarts(filters.from) : undefined,
        queuedBefore: filters.to ? dayEnds(filters.to) : undefined,
        limit: Math.min(shown, MOST),
      })
      .then((found) => live && setHistory(found))
      .catch((e) => live && setError(said(e)))
    return () => {
      live = false
    }
  }, [filters, shown, reads])

  // New runs appear as the worker takes them, while the list is what is open.
  useEffect(() => {
    if (open) return
    const every = window.setInterval(() => setReads((n) => n + 1), 15_000)
    return () => window.clearInterval(every)
  }, [open])

  function narrow(change: Partial<Filters>) {
    setFilters((f) => ({ ...f, ...change }))
    setShown(PAGE)
  }

  async function details(run: JobRun) {
    setOpening(run.id)
    setError(null)
    setNotice(null)
    try {
      setOpen(await api.jobRun(run.id))
    } catch (e) {
      setError(said(e))
    } finally {
      setOpening(null)
    }
  }

  async function openRun(id: number) {
    setError(null)
    setNotice(null)
    try {
      setOpen(await api.jobRun(id))
    } catch (e) {
      setError(said(e))
    }
  }

  async function retry(file: FeedFile) {
    if (!open) return
    setError(null)
    try {
      const { job_id: run } = await api.retryFeedFile(file.id)
      setNotice(
        run
          ? `${file.name} is being read again in run ${run}. Its menu opens that run.`
          : `${file.name} is read again once automatic loading is turned on.`,
      )
      setOpen(await api.jobRun(open.id))
    } catch (e) {
      setError(said(e))
    }
  }


  /** What can be done with one thing a run produced, from its row's menu. */
  function productActions(product: JobProduct): MenuItem[] {
    const items: MenuItem[] = []
    if (product.type === 'snapshot' && onOpenVerdicts && canOpen('run')) {
      items.push({ label: 'Open verdicts', onSelect: () => onOpenVerdicts(product.id) })
    }
    if (canOpen(product.type)) {
      items.push({ label: PRODUCT_ACTION[product.type], onSelect: () => onOpen(product) })
    }
    return items
  }

  /** What can be done about one file a pass collected, from its row's menu. */
  function fileActions(file: CollectedFile): MenuItem[] {
    if (file.later_run) {
      const later = file.later_run
      return [{ label: `Open run ${later}`, onSelect: () => void openRun(later) }]
    }
    if (file.later_status) return []
    const items: MenuItem[] = []
    if (file.status === 'loaded' && canOpen('snapshot')) {
      for (const s of file.snapshots) {
        const label = snapshotLabel(s)
        items.push({ label: `Open ${label}`, onSelect: () => onOpen({ type: 'snapshot', id: s.id, label, note: null }) })
      }
    }
    if (file.status === 'awaiting_mapping' && onMapLayout) {
      items.push({ label: 'Map this layout', onSelect: onMapLayout })
    }
    if (canRetryFiles && (file.status === 'refused' || file.status === 'awaiting_mapping')) {
      items.push({ label: 'Try again', onSelect: () => void retry(file) })
    }
    return items
  }

  const errorLine = error && (
    <p className="error" role="alert">
      {error}
    </p>
  )

  // One run, in place of the list.
  if (open) {
    const made = open.produced ?? []
    const duration = took(open)
    return (
      <section className="job-history" data-testid="job-run">
        <header className="page-head">
          <div className="fund-view-bar">
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setOpen(null)
                setNotice(null)
              }}
            >
              ← All runs
            </button>
          </div>
          <h2>{open.label}</h2>
        </header>
        {errorLine}
        {notice && <p className="ok">{notice}</p>}

        <div className="card">
          <dl className="job-facts">
            <dt>State</dt>
            <dd>
              <JobLine run={open} />
            </dd>
            <dt>Queued</dt>
            <dd>
              {when(open.enqueued_at)}
              {open.requested_by ? `, run now by ${open.requested_by}` : ', on schedule'}
            </dd>
            <dt>Started</dt>
            <dd>{when(open.started_at)}</dd>
            <dt>Finished</dt>
            <dd>
              {when(open.finished_at)}
              {duration && `, after ${duration}`}
            </dd>
            <dt>Attempts</dt>
            <dd>
              {open.attempts} of {open.max_attempts}
            </dd>
            {open.worker && (
              <>
                <dt>Worker</dt>
                <dd>{open.worker}</dd>
              </>
            )}
          </dl>
        </div>

        {made.length > 0 && (
          <div className="card" data-testid="job-produced">
            <h3>What it produced</h3>
            <div className="table-scroll">
              <table className="grid stacked">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th>Desk</th>
                    <th>What</th>
                    <th>Detail</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {made.map((p) => {
                    const actions = productActions(p)
                    return (
                      <tr key={`${p.type}:${p.id}`} data-testid="job-product">
                        <td>{p.fund ? <strong>{p.fund}</strong> : '—'}</td>
                        <td>{p.desk ?? ''}</td>
                        <td>{p.what ?? p.label}</td>
                        <td>{p.note ?? ''}</td>
                        <td className="run-actions">
                          {actions.length > 0 && <RowMenu label={`Actions for ${p.label}`} items={actions} />}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {open.files && (
          <div className="card" data-testid="job-files">
            <h3>Files collected</h3>
            {open.files.length === 0 ? (
              <p className="empty">No new file was waiting.</p>
            ) : (
              <div className="table-scroll">
                <table className="grid stacked">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Outcome</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {open.files.map((f) => (
                      <tr key={f.id}>
                        <td>
                          <strong>{f.name}</strong>
                        </td>
                        <td>
                          <span className={`feed-status ${f.status}`}>{OUTCOME[f.status]}</span>
                          {f.kind === 'benchmark_figures' && <span className="clause">Benchmark figures</span>}
                          {f.status === 'loaded' && f.figures
                            ? [
                                <span key="figures" className="clause" data-testid="file-figures">
                                  {f.figures.benchmarks.join(', ')}: {f.figures.rows} figures as at{' '}
                                  {f.figures.days.join(', ')}
                                </span>,
                              ]
                            : f.status === 'loaded'
                            ? f.snapshots.map((s) => (
                                <span key={s.id} className="clause">
                                  {snapshotLabel(s)}
                                </span>
                              ))
                            : f.detail.map((reason, i) => (
                                <span key={i} className="clause">
                                  {sentence(reason)}
                                </span>
                              ))}
                          {f.later_status && (
                            <span className="clause file-later" data-testid="file-later">
                              {!f.later_run
                                ? 'Waiting to be read again on the next pass'
                                : f.later_status === 'retrying'
                                  ? `Being read again in run ${f.later_run}`
                                  : `Read again in run ${f.later_run}: ${OUTCOME[f.later_status]}`}
                            </span>
                          )}
                        </td>
                        <td className="run-actions">
                          {fileActions(f).length > 0 && (
                            <RowMenu label={`Actions for ${f.name}`} items={fileActions(f)} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {open.error && (
          <div className="card">
            <h3>Why it failed</h3>
            <p className="error">{open.error}</p>
          </div>
        )}

        {open.result && (
          <details className="card">
            <summary>Everything the run recorded</summary>
            <pre className="job-result">{JSON.stringify(open.result, null, 2)}</pre>
          </details>
        )}
      </section>
    )
  }

  const runs = history?.runs ?? []
  return (
    <section className="job-history">
      <header className="page-head">
        <h2>Scheduled Jobs</h2>
        <p className="note">
          Every run of the scheduled jobs in the last month, what each came to, and what it produced.
        </p>
      </header>
      {errorLine}

      <div className="filters" data-testid="job-filters">
        <label>
          Job
          <select value={filters.kind} onChange={(e) => narrow({ kind: e.target.value })}>
            <option value="">Every job</option>
            {(history?.jobs ?? []).map((j) => (
              <option key={j.kind} value={j.kind}>
                {j.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          State
          <select value={filters.state} onChange={(e) => narrow({ state: e.target.value })}>
            <option value="">Any state</option>
            {Object.entries(JOB_STATUS).map(([state, label]) => (
              <option key={state} value={state}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Queued from
          <input type="date" value={filters.from} onChange={(e) => narrow({ from: e.target.value })} />
        </label>
        <label>
          Queued to
          <input type="date" value={filters.to} onChange={(e) => narrow({ to: e.target.value })} />
        </label>
        {filtered && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setFilters(NONE)
              setShown(PAGE)
            }}
          >
            Clear
          </button>
        )}
      </div>

      {history === null ? (
        !error && <p className="loading">Loading…</p>
      ) : runs.length === 0 ? (
        <p className="empty">{filtered ? 'No run matches these filters.' : 'Nothing has run yet.'}</p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="grid stacked" data-testid="job-runs">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Desks</th>
                  <th>Queued</th>
                  <th>Outcome</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  // Details only: what a run produced is read there, fund by fund, before opening any of it.
                  const items: MenuItem[] = [{ label: 'Details', onSelect: () => void details(run) }]
                  return (
                    <tr key={run.id}>
                      <td>
                        {run.label}
                        <span className="clause">
                          {run.requested_by ? `Run now by ${run.requested_by}` : 'On schedule'}
                        </span>
                      </td>
                      <td data-testid="run-desks">{(run.desks ?? []).join(', ')}</td>
                      <td>{when(run.enqueued_at)}</td>
                      <td>
                        <JobLine run={run} />
                      </td>
                      <td className="run-actions">
                        <RowMenu
                          label={`Run ${run.id}: ${run.label}`}
                          disabled={opening === run.id}
                          items={items}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="note" data-testid="job-count">
            {history.total > runs.length
              ? `Showing the latest ${runs.length} of ${history.total}. `
              : `${history.total} run${history.total === 1 ? '' : 's'}.`}
            {history.total > runs.length && shown < MOST && (
              <button type="button" className="ghost" onClick={() => setShown((n) => Math.min(n + PAGE, MOST))}>
                Show more
              </button>
            )}
          </p>
        </>
      )}
    </section>
  )
}
